import Drawer from "../Drawer";
import { arrayHasItems } from "../../helpers/common";
import { enumStatuses } from "../../consts/status";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

const poDetailColumns = [
    { label: "PO Number", width: "min-w-[105px]" },
    { label: "PO Date", width: "min-w-[85px]" },
    { label: "Status", width: "w-[120px]" },
    { label: "Unit Cost", width: "min-w-[95px]" },
    { label: "BOM packaging", width: "min-w-[135px]" },
    { label: "Landed Cost", width: "min-w-[125px]" },
    { label: "Tariff Each", width: "min-w-[115px]" },
    { label: "Shipping Each", width: "min-w-[125px]" },
    { label: "Weighted Cost", width: "min-w-[125px]" },
    { label: "Received/Ordered", width: "min-w-[140px]" },
    { label: "Difference", width: "min-w-[180px]" },
];

const getControlToneClassName = (product) => {
    const baseClasses =
        "inline-block max-w-[136px] truncate rounded-md border px-2 py-0.5 text-[10px] font-medium shadow-none";

    if (!product) {
        return `${baseClasses} border-slate-200 bg-slate-100 text-slate-700`;
    }
    if (product.missingBomFlag) {
        return `${baseClasses} border-rose-200 bg-rose-50 text-rose-700`;
    }
    if (product.missingFreightFlag || product.largeVarianceFlag) {
        return `${baseClasses} border-amber-200 bg-amber-50 text-amber-700`;
    }
    if (product.weightedCost == null || product.weightedCost === "") {
        return `${baseClasses} border-slate-200 bg-slate-100 text-slate-700`;
    }
    return `${baseClasses} border-emerald-200 bg-emerald-50 text-emerald-700`;
};

const formatDisplayMoney = (value) => {
    if (value == null || value === "" || value === "-") {
        return "N/A";
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return String(value);
    }
    return `$${numericValue.toFixed(2)}`;
};

const formatSignedCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }
    const numericValue = Number(value);
    const absoluteValue = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    }).format(Math.abs(numericValue));
    if (numericValue > 0) return `+${absoluteValue}`;
    if (numericValue < 0) return `-${absoluteValue}`;
    return absoluteValue;
};

const parsePoDifference = (groupItem) => {
    const rawDifference = groupItem?.differenceData?.difference;
    if (rawDifference == null || rawDifference === "" || rawDifference === "-") {
        return null;
    }
    const numericValue = Number(rawDifference);
    return Number.isFinite(numericValue) ? numericValue : null;
};

export const ProductRowDetails = ({
    marginFromSidebar,
    drawerWidth,
    isOpen,
    onClose,
    selectedProduct,
    onBackToWatchlist,
}) => {
    const groupedByPoId = selectedProduct?.groupedByPoId || [];

    const productName = selectedProduct?.name || selectedProduct?.productName || "—";
    const executionSku = selectedProduct?.sku || "—";
    const variantName = selectedProduct?.variant || "Base";
    const supplier = selectedProduct?.supplier || "Unknown supplier";
    const canonicalCost = selectedProduct?.canonicalCost || "—";
    const controlStatus = selectedProduct?.controlStatus || "—";

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            marginFromSidebar={marginFromSidebar}
            width={drawerWidth}
            headerContent={
                <div className="flex items-start justify-between gap-3 pr-8">
                    <div>
                        <h2 className="font-semibold text-lg tracking-[-0.02em] text-primary">
                            {`${executionSku} PO cost details`.trim()}
                        </h2>
                        <p className="text-sm mt-1 max-w-[520px] leading-6 text-gray-600">
                            PO-level cost history and differences for this execution SKU from the COGS preview response.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onBackToWatchlist}
                        className="inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-border bg-white px-3 text-sm font-medium text-primary shadow-xs transition hover:bg-gray-50"
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        Watchlist
                    </button>
                </div>
            }
        >
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                <div className="space-y-4">
                    <div className="rounded-lg border border-border bg-background px-4 py-3">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-base font-semibold tracking-[-0.02em] text-foreground">
                                    {productName}
                                </p>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    {executionSku} · {variantName} · {supplier}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="inline-flex items-center rounded-md border border-border bg-white px-2 py-0.5 text-[11px] font-medium text-foreground shadow-none">
                                    Canonical Cost {canonicalCost}
                                </span>
                                <span className={getControlToneClassName(selectedProduct)}>
                                    {controlStatus}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-lg border border-border bg-white">
                        {!arrayHasItems(groupedByPoId) ? (
                            <div className="px-4 py-10 text-center text-sm text-gray-500">
                                No PO cost history found for this product.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full table-fixed border-separate border-spacing-0 text-[13px]">
                                    <thead className="sticky top-0 z-10 bg-white">
                                        <tr>
                                            {poDetailColumns.map((column) => (
                                                <th
                                                    className={`h-11 border-b border-border px-2 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground first:pl-3 last:pr-3 ${column.width}`}
                                                    key={column.label}
                                                >
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groupedByPoId.map((groupItem, index) => {
                                            const difference = parsePoDifference(groupItem);
                                            return (
                                            <tr
                                                className="hover:bg-background/50"
                                                key={`${groupItem.poId}_${index}`}
                                            >
                                                <td className="border-b border-transparent px-2.5 py-2.5 align-middle first:pl-3">
                                                    <a
                                                        href={`/finance?search=${groupItem.poNumber}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-left text-[13px] font-medium text-sky-700 transition hover:underline"
                                                    >
                                                        {groupItem.poNumber}
                                                    </a>
                                                </td>
                                                <td className="border-b border-transparent px-2 py-2.5 align-middle text-[13px] text-foreground">
                                                    {groupItem.poDate || "—"}
                                                </td>
                                                <td className="border-b border-transparent px-2 py-2.5 align-middle text-[13px] text-foreground">
                                                    {enumStatuses[groupItem.status] || groupItem.status || "—"}
                                                </td>
                                                <td className="border-b border-transparent px-2 py-2.5 align-middle text-[13px] text-foreground">
                                                    {formatDisplayMoney(groupItem.unitPerCost)}
                                                </td>
                                                <td className="border-b border-transparent px-2 py-2.5 align-middle text-[13px] text-foreground">
                                                    {formatDisplayMoney(groupItem.BOMByProduct)}
                                                </td>
                                                <td className="border-b border-transparent px-2 py-2.5 align-middle text-[13px] text-foreground">
                                                    {formatDisplayMoney(groupItem.landedCost)}
                                                </td>
                                                <td className="border-b border-transparent px-2 py-2.5 align-middle text-[13px] text-foreground">
                                                    {formatDisplayMoney(groupItem.tariffCost)}
                                                </td>
                                                <td className="border-b border-transparent px-2 py-2.5 align-middle text-[13px] text-foreground">
                                                    {formatDisplayMoney(groupItem.shippingCost)}
                                                </td>
                                                <td className="border-b border-transparent px-2 py-2.5 align-middle text-[13px] text-foreground">
                                                    {formatDisplayMoney(groupItem.weightedCost)}
                                                </td>
                                                <td className="border-b border-transparent px-2 py-2.5 align-middle text-[13px] text-foreground">
                                                    {groupItem.rangeReceivedBoxes || "N/A"}
                                                </td>
                                                <td className="border-b border-transparent px-2 py-2.5 align-middle last:pr-3">
                                                    {difference === null ? (
                                                        <div className="text-[13px] font-medium text-foreground">—</div>
                                                    ) : (
                                                        <div
                                                            className={`text-[16px] font-semibold tracking-[-0.02em] ${
                                                                difference > 0
                                                                    ? "text-emerald-800"
                                                                    : difference < 0
                                                                      ? "text-rose-700"
                                                                      : "text-foreground"
                                                            }`}
                                                        >
                                                            {formatSignedCurrency(difference)}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Drawer>
    );
};
